/**
 * MobileMissionHistoryModal
 *
 * Mobile full-screen MUI Dialog replacement for the desktop MissionHistoryModal.
 * Displays mission history grouped by status (upcoming, ongoing, past) as card-based lists.
 * Slide-up transition, sticky AppBar with close button.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Slide,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getCollabMissions, setCollabMissions } from "../../../components/teams/teamsSlice";
import moment from "moment";
import { Localize } from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const sectionHeaderSx = {
  fontWeight: 700,
  fontSize: "0.85rem",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  px: 2,
  pt: 2,
  pb: 0.75,
};

const cardSx = {
  mx: 1.5,
  mb: 1,
  borderRadius: "14px",
  backgroundColor: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  transition: TRANSITION_SMOOTH,
  "&:active": {
    transform: "scale(0.98)",
  },
};

/* ------------------------------------------------------------------ */
/*  Status config                                                      */
/* ------------------------------------------------------------------ */
const STATUS_CONFIG = {
  upcoming: { label: "À venir", color: "#3c1053", chipColor: "secondary" },
  ongoing: { label: "En cours", color: "#041e42", chipColor: "primary" },
  past: { label: "Terminées", color: "#4c8c2b", chipColor: "success" },
};

/* ------------------------------------------------------------------ */
/*  Helper: format date                                                */
/* ------------------------------------------------------------------ */
function formatDate(value) {
  if (!value) return "N/A";
  const d = moment(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : "N/A";
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param {Object}   props
 * @param {boolean}  props.visible  - controls dialog open state
 * @param {Function} props.setVisible - toggle visibility
 * @param {string|number} props.collabId - collaborator id for fetching missions
 */
export default function MobileMissionHistoryModal({ visible, setVisible, collabId }) {
  const dispatch = useDispatch();
  const missionHistory = useSelector((state) => state.teams.collabMissions);

  const [grouped, setGrouped] = useState({
    upcoming: [],
    ongoing: [],
    past: [],
  });

  // Fetch missions when opened
  useEffect(() => {
    if (visible && collabId) {
      dispatch(getCollabMissions({ id_collab: collabId }));
    }
  }, [visible, collabId, dispatch]);

  // Group missions by status
  useEffect(() => {
    if (missionHistory) {
      setGrouped({
        upcoming: missionHistory.upcomingMissions || [],
        ongoing: missionHistory.ongoingMissions || [],
        past: missionHistory.pastMissions || [],
      });
    }
  }, [missionHistory]);

  const handleClose = () => {
    setVisible(false);
    setGrouped({ upcoming: [], ongoing: [], past: [] });
    dispatch(setCollabMissions([]));
  };

  const totalMissions =
    grouped.upcoming.length + grouped.ongoing.length + grouped.past.length;

  const renderMissionCard = (mission, index, statusKey) => {
    const cfg = STATUS_CONFIG[statusKey];
    return (
      <Fade in timeout={200 + index * 60} key={`${statusKey}-${index}`}>
        <Card sx={cardSx} variant="outlined">
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            {/* Team + Poste */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <WorkOutlineIcon
                sx={{ fontSize: 18, color: "rgba(0,0,0,0.45)" }}
              />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  color: "rgba(0,0,0,0.87)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {mission.team_name || "—"}
              </Typography>
            </Box>

            {mission.poste_name && (
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: "rgba(0,0,0,0.6)",
                  ml: 3.25,
                  mb: 0.75,
                }}
              >
                {mission.poste_name}
              </Typography>
            )}

            {/* Dates row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                ml: 3.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }}
                />
                <Typography
                  sx={{ fontSize: "0.78rem", color: "rgba(0,0,0,0.55)" }}
                >
                  {formatDate(mission.start_date)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <EventBusyOutlinedIcon
                  sx={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }}
                />
                <Typography
                  sx={{ fontSize: "0.78rem", color: "rgba(0,0,0,0.55)" }}
                >
                  {formatDate(mission.end_date)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  const renderSection = (statusKey) => {
    const missions = grouped[statusKey];
    if (!missions || missions.length === 0) return null;
    const cfg = STATUS_CONFIG[statusKey];

    return (
      <Box key={statusKey}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ...sectionHeaderSx }}>
          <Chip
            label={`${cfg.label} (${missions.length})`}
            size="small"
            color={cfg.chipColor}
            variant="outlined"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              height: 26,
              borderRadius: "8px",
            }}
          />
        </Box>
        {missions.map((m, i) => renderMissionCard(m, i, statusKey))}
      </Box>
    );
  };

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          backgroundColor: "#f8f9fa",
        },
      }}
    >
      {/* Sticky AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px) saturate(1.4)",
          WebkitBackdropFilter: "blur(20px) saturate(1.4)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          color: "rgba(0,0,0,0.87)",
        }}
      >
        <Toolbar sx={{ minHeight: 56, px: 1 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            sx={{
              minWidth: 44,
              minHeight: 44,
              borderRadius: "12px",
              color: "rgba(0,0,0,0.7)",
              transition: TRANSITION_SMOOTH,
              "&:active": { transform: "scale(0.94)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              flex: 1,
              ml: 0.5,
              letterSpacing: "-0.01em",
            }}
          >
            Historique des missions
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pb: 3,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {totalMissions === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.95rem" }}>
              Aucun historique de mission trouvé.
            </Typography>
          </Box>
        ) : (
          <>
            {renderSection("ongoing")}
            {renderSection("upcoming")}
            {renderSection("past")}
          </>
        )}
      </Box>
    </Dialog>
  );
}
